import * as React from 'react';
import { append, map, remove } from 'ramda';
import * as Qs from 'query-string';
import throttle from 'lodash.throttle';
import { Helmet } from 'react-helmet';
import { translate } from '../../translations';
import { edgeLanguageFilter } from '../../../pages/utils/edge-language-filter';
import { Layout, Animation, Translate, Slider, ArticleCard } from '../../components';
import {
  fixContentfulAssetPath,
  localizeLink,
  makeTextUrlFriendly,
  startPageAnimation,
} from '../../utils';
import { FilterMapping, UrlFriendlyFilters } from '../../data/blog-filters-mapping';
import { CarouselPostTemplate } from '../blog-landing-page/carousel-post-template';
import { LandingPageProperties, LandingPageState } from '../../interfaces/landing-page';
import { LandingPageFilterMenu } from './landing-filter-menu';

import './landing-page.scss';

const mapUrlFriendly = (label: string): string => makeTextUrlFriendly(label.toLowerCase());

class LandingPage extends React.Component<LandingPageProperties, LandingPageState> {
  state: LandingPageState = {
    filters: [],
    pagination: 0,
    compactHeader: false,
  };

  preventNextHistoryBack = false;

  private loadMoreEndReference: HTMLElement = null as any;

  get AllPosts() {
    return this.props.data.allContentfulBlogPost.edges.filter(edgeLanguageFilter).map(x => x.node);
  }

  get CarouselPosts() {
    return this.AllPosts.slice(0, 3);
  }

  get AllFilteredPosts() {
    const filters = map(x => FilterMapping[x], this.state.filters);
    return this.AllPosts.filter(
      post =>
        post.labels &&
        filters.every(orFilters => orFilters.some(orFilter => post.labels.includes(orFilter))),
    );
  }

  get ActivePosts() {
    return this.AllFilteredPosts.slice(0, 12 + 6 * this.state.pagination);
  }

  get HasMorePosts() {
    return this.AllFilteredPosts.length > this.ActivePosts.length;
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.onHistoryBack);
    window.removeEventListener('scroll', this.onScroll);
  }

  componentDidMount() {
    startPageAnimation();
    window.addEventListener('popstate', this.onHistoryBack);
    window.addEventListener('scroll', this.onScroll);
    const filtersFromQuery = this.getFiltersFromQueryString();

    if (filtersFromQuery.length) {
      this.setState(() => ({ filters: filtersFromQuery }));
    }
  }

  onHistoryBack = () => {
    if (this.preventNextHistoryBack) {
      this.preventNextHistoryBack = false;
      return;
    }
    this.setState(() => ({ filters: this.getFiltersFromQueryString() }));
  };

  onScroll = throttle(() => {
    if (this.loadMoreEndReference.getBoundingClientRect().bottom < 850) {
      this.loadMorePosts();
    }
  }, 400);

  getFiltersFromQueryString = () => {
    const filters = Qs.parse(this.props.location.search, { arrayFormat: 'index' })
      .categories as string[];

    return (filters || []).map(filter => UrlFriendlyFilters[filter]);
  };

  updateRoute = () => {
    const search = Qs.stringify(
      { categories: this.state.filters.map(mapUrlFriendly) },
      { arrayFormat: 'index' },
    );
    this.preventNextHistoryBack = true;
    const entryType = this.props.pageContext.entryType;
    const pathName = window.location.pathname;
    const isEntityTypeInPath = !!pathName.match(entryType);
    window.history.pushState(
      null,
      this.props.data.site.siteMetadata.title,
      (!isEntityTypeInPath ? entryType + '/' : '') + (search ? `?${search}` : ''),
    );
  };

  clearFilters = () => {
    this.setState(() => ({ filters: [], pagination: 0 }), this.updateRoute);
  };

  toggleFilter = input => {
    let { filters } = this.state;
    const labelIndex = filters.findIndex(x => x === input);

    if (labelIndex !== -1) {
      filters = remove(labelIndex, 1, filters);
    } else {
      filters = append(input, filters);
    }

    this.setState(() => ({ filters, pagination: 0 }), this.updateRoute);
  };

  loadMorePosts = () => {
    if (this.HasMorePosts) {
      this.setState(state => ({ pagination: state.pagination + 1 }));
    }
  };

  render() {
    const secondaryMenu =
      this.props.filterCategories.length > 0 ? (
        <LandingPageFilterMenu
          toggleFilter={this.toggleFilter}
          categories={this.props.filterCategories}
          activeFilters={this.state.filters}
        />
      ) : undefined;
    const locale = this.props.pageContext.locale;
    const entryType = this.props.pageContext.entryType;

    const canonical = `${this.props.data.site.siteMetadata.siteUrl}${localizeLink(
      locale,
      `/${entryType}`,
    )}`;

    const title = translate(locale, this.props.title);
    const description = translate(locale, this.props.description);
    const bannerImage = this.props.bannerImage;

    return (
      <Layout
        headerDarkTheme={false}
        title={title}
        secondaryMenu={secondaryMenu}
        englishCanonicalUrl={`/${entryType}`}
        locale={locale}
      >
        <Helmet>
          <link rel="canonical" href={canonical} />
          <meta name="description" content={description} />
        </Helmet>
        <div className="landing-page-wrapper">
          <div
            className={
              'landing-page ' +
              (this.props.filterCategories.length > 0 ? 'spacing-top-big' : 'spacing-top-md')
            }
            ref={ref => {
              this.loadMoreEndReference = ref!;
            }}
          >
            {bannerImage ? (
              <div className="banner-wrapper">
                <img src={bannerImage} alt="banner" />
              </div>
            ) : null}

            <div className="tablet-hide mobile-hide landing__carousel">
              {this.CarouselPosts.length > 0 && (
                <Slider
                  items={this.CarouselPosts}
                  itemsPerSlide={1}
                  rotateSlides={true}
                  itemTemplate={CarouselPostTemplate}
                  path={entryType}
                />
              )}
            </div>
            <div className="landing-page--content">
              <div className="landing-page--listing">
                {this.ActivePosts.map((post, index) => {
                  return (
                    <Animation key={`post-${index}`}>
                      <ArticleCard
                        key={localizeLink(post.fields.locale, `/${entryType}/${post.slug}`)}
                        image={post.heroImage && fixContentfulAssetPath(post.heroImage.file.url)}
                        imageAlt={post.heroImage && post.heroImage.title}
                        title={post.title}
                        excerpt={post.excerpt}
                        link={localizeLink(post.fields.locale, `/${entryType}/${post.slug}`)}
                      />
                    </Animation>
                  );
                })}

                {this.ActivePosts.length <= 0 && (
                  <EmptyResults
                    filters={this.state.filters}
                    toggleFilter={this.toggleFilter}
                    clearFilters={this.clearFilters}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }
}

/* eslint-disable jsx-a11y/anchor-is-valid */
const EmptyResults = ({ filters, toggleFilter, clearFilters }) => (
  <div className="landing-page--empty">
    <h2>
      <Translate id="page.bloglanding.noResult" />
    </h2>
    {filters.map(filter => (
      <a href="#" key={filter} onClick={() => toggleFilter(filter)}>
        <button className="btn btn--secondary">×{filter}</button>
      </a>
    ))}
    <p>
      <Translate id="page.bloglanding.noResultHelp" />{' '}
      <a href="#" onClick={e => clearFilters(e)} className="color-primary">
        <Translate id="page.bloglanding.noResultClearFilters" />
      </a>
    </p>
  </div>
);

export default LandingPage;
/* eslint-enable jsx-a11y/anchor-is-valid */
